import React, { useEffect, useRef } from "react";

export const getCategoryAndSubCategory = (location, pageType, config = {}, story) => {
  const pathname = location.pathname;
  const pathParts = pathname.split("/").filter(Boolean); // removes empty strings
  // Default values
  let category = "NA";
  let subCategory = "NA";
  let contentId = "NA";

  // Home page
  if (pageType === "home-page" || pathname === "/") {
    return { category: "NA", subCategory: "NA", contentId: "NA" };
  }

  // short-news
  if (pageType === "summary-page" || pathname === "/") {
    return { category: "NA", subCategory: "NA", contentId: "NA" };
  }

  // Set content_id if story exists and has an id
  if (story && story.id) {
    contentId = story?.["public-identifier"];
  }

  // Visual Stories
  if (pathname.includes("visual-stories")) {
    return { category: "visual-stories", subCategory: "NA", contentId };
  }

  // Videos
  if (pathname.includes("videos")) {
    return { category: "videos", subCategory: "NA", contentId };
  }

  // Topic pages
  if (pathname.startsWith("/tag/")) {
    return {
      category: "topic",
      subCategory: pathParts[1] || "NA",
      contentId: contentId,
    };
  }

  // Search page
  if (pathname.startsWith("/search")) {
    return { category: "search", subCategory: "NA", contentId: contentId };
  }

  // Author pages
  if (pathname.startsWith("/author")) {
    if (pathname === "/author") {
      return { category: "author", subCategory: "NA", contentId: contentId };
    }
    return {
      category: "author",
      subCategory: pathParts[1] || "NA",
      contentId: contentId,
    };
  }

  // Magazine pages
  if (pathname.startsWith("/magazines")) {
    if (pathname === "/magazines") {
      return { category: "magazines", subCategory: "NA", contentId };
    }
    return {
      category: "magazines",
      subCategory: pathParts[1] || "NA",
      contentId: contentId,
    };
  }
  if (pathname.startsWith("/magazine")) {
    if (pathname === "/magazine") {
      return { category: "magazine", subCategory: "NA", contentId: "NA" };
    }
    return {
      category: "magazine",
      subCategory: pathParts[1] || "NA",
      contentId: contentId,
    };
  }
  // Story pages with section data
  if (pageType === "story-page" && story?.sections?.[0]) {
    const firstSection = story?.sections?.[0];
    const configSections = config.sections || [];

    if (firstSection["parent-id"] === null) {
      // If parent-id is null, use the section slug as category
      category = firstSection?.slug;
      subCategory = "NA";
    } else {
      // Find parent section from config
      const parentSection = configSections.find((section) => section.id === firstSection["parent-id"]);
      if (parentSection) {
        category = parentSection?.slug;
        subCategory = firstSection?.slug;
      } else {
        // Fallback to section name if parent not found
        category = firstSection?.slug;
        subCategory = "NA";
      }
    }
    // Set content_id if story exists and has an id
    contentId = story?.["public-identifier"];

    return { category, subCategory, contentId };
  }

  // Section pages
  if (
    pathParts.length > 0 &&
    (pageType === "section-page")
  ) {
    category = pathParts[0];
    if (pathParts[1] && !pathParts[1].match(/\d+/)) {
      subCategory = pathParts[1];
    }
  }
  // Ranking pages
  if (
    pathParts.length > 0 &&
    (
      pageType === "section-page")
  ) {
    category = pathParts[0];
    if (pathParts[1]) {
      subCategory = pathParts[1];
    }
  }
  return { category, subCategory, contentId };
};

export const getTemplateName = (name) => {
  const templateMap = {
    "live-blog": "liveblog",
    photo: "photostory",
    "book-review": "bookreview",
    text: "articlestory",
    video: "videostory",
    magazine: "magazinestory",
    sponsored: "sponsoredstory",
    "home-page": "home",
    "tag-page": "listing",
    "search-page": "listing",
    "section-page": "listing",
    "authors-list": "listing",
    "author-page": "authordetail",
    "static-page": "others",
    "summary-page": "listing",
    "all-magazines-listing-page": "listing",
    "magazines-issue-landing-page": "edition",
    default: "NA",
  };

  return templateMap[name] || templateMap.default;
};

export const handleAuthorInteraction = (author, url, pageType = "story-page", config = {}, story = {}) => {
  const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "dl_story_interactions",
      event_action: "author_click",
      link_url: url,
      link_text: author?.name,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  }
};

export const trackPodcastInteraction = (options) => {
  if (typeof window === "undefined") return;

  const { eventAction, eventLabel, pageType, location, config, story, templateName } = options;

  window.dataLayer = window.dataLayer || [];

  if (!location) return;

  const { category, subCategory } = getCategoryAndSubCategory(location, pageType, config, story);

  window.dataLayer.push({
    event: "podcast_interactions",
    event_action: eventAction || "NA",
    event_label: eventLabel || "NA",
    category: category || "NA",
    sub_category: subCategory || "NA",
    content_id: "NA",
    template_name: (templateName || getTemplateName(story?.["story-template"] || pageType) || "NA").toLowerCase(),
    platform: window.innerWidth >= 768 ? "desktop" : "mobile",
  });
};

/**
 * Track component impressions when they become visible in the viewport
 * @param {Object} options - Configuration options
 * @param {string} options.sectionName - Name of the section or component (e.g., "Recommended Stories")
 * @param {string} options.pageType - Type of the page (e.g., "home-page", "story-page")
 * @param {Object} options.location - Window location object
 * @param {Object} options.config - QT config object
 * @param {Object} options.story - Story object (optional)
 */
/**
 * Tracks CTA click events using the dataLayer
 * @param {Object} options - Configuration options
 * @param {string} options.linkText - Text of the clicked link/button
 * @param {string} options.linkUrl - URL of the link (optional)
 * @param {string} options.linkCategory - Category of the link (optional)
 * @param {string} options.pageType - Current page type
 * @param {Object} options.location - Window location object
 * @param {Object} options.config - QT config object (optional)
 * @param {Object} options.story - Story object (optional)
 * @param {Function} options.eventCallback - Callback function to execute after tracking (optional)
 */
export const trackCtaClick = (options) => {
  if (typeof window === "undefined") return;

  const { linkText, linkUrl, linkCategory, pageType, location, config, story, eventCallback } = options;

  if (window.dataLayer) {
    if (!location) return;

    const { category, subCategory, contentId } = getCategoryAndSubCategory(location, pageType, config, story);
    const templateName = getTemplateName(story?.["story-template"] || pageType);

    window.dataLayer.push({
      event: "dl_cta_click",
      link_text: linkText?.toLowerCase() || "NA",
      link_url: linkUrl || "NA",
      link_category: linkCategory || "NA",
      category: category,
      sub_category: subCategory,
      content_id: contentId || "NA",
      template_name: templateName?.toLowerCase() || "NA",
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      eventCallback: eventCallback,
    });
  }
};

export const trackWidgetImpression = (options) => {
  if (typeof window === "undefined") return;

  const { sectionName, pageType, location, config, story } = options;

  if (window.dataLayer) {
    if (!location) return;

    const { category, subCategory, contentId } = getCategoryAndSubCategory(location, pageType, config, story);
    const templateName = getTemplateName(story?.["story-template"] || pageType);

    window.dataLayer.push({
      event: "dl_widget_impression",
      section_name: sectionName?.toLowerCase() || "NA",
      category: category,
      sub_category: subCategory,
      content_id: contentId || "NA",
      template_name: templateName?.toLowerCase() || "NA",
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  }
};

/**
 * Custom hook to track widget impressions using IntersectionObserver
 * @param {Object} options - Configuration options
 * @param {string} options.sectionName - Name of the section or component
 * @param {string} options.pageType - Type of the page
 * @param {Object} options.location - Window location object
 * @param {Object} options.config - QT config object
 * @param {Object} options.story - Story object (optional)
 * @param {number} options.threshold - Visibility threshold (0-1, default: 0.2)
 * @returns {Object} - ref to attach to the component
 */
export const useTrackWidgetImpression = (options, hookOptions = {}) => {
  // Always create the refs regardless of disabled state
  const componentRef = useRef(null);
  const impressionTracked = useRef(false);
  const isDisabled = hookOptions.disabled;

  // Extract options, providing defaults if needed
  const { sectionName, pageType, location, config, story, threshold = 0.2 } = options || {};

  const isBrowser = typeof window !== "undefined";

  // Define impression handler
  const handleImpression = () => {
    // Only track impressions in browser environment and if not disabled
    if (isBrowser && !impressionTracked.current && !isDisabled) {
      trackWidgetImpression({
        sectionName,
        pageType,
        location,
        config,
        story,
      });
      impressionTracked.current = true;
    }
  };

  // Always use the same effect structure
  useEffect(() => {
    // Skip setup if browser not available or hook is disabled
    if (!isBrowser || isDisabled) return;

    // Only proceed if IntersectionObserver is available
    if ("IntersectionObserver" in window) {
      const observerOptions = {
        threshold, // Track when component is visible at the specified threshold
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            handleImpression();
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      if (componentRef.current) {
        observer.observe(componentRef.current);
      }

      return () => {
        if (componentRef.current) {
          observer.unobserve(componentRef.current);
        }
      };
    }
  }, [isDisabled, threshold, sectionName]); // Add dependencies to avoid lint warnings

  return componentRef;
};
